import React from 'react';
import { MessageCircle, Calendar, User, Paperclip } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FeedbackCardProps } from '@/types/feedback';
import VotingComponent from './VotingComponent';

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  showVoting = true,
  showComments = true,
  onClick,
  className,
}) => {
  const handleCardClick = () => {
    if (onClick) {
      onClick(feedback);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card
      className={cn(
        'transition-all duration-200 hover:shadow-md cursor-pointer',
        'border border-border hover:border-primary/20',
        className
      )}
      onClick={handleCardClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {/* Voting Component */}
          {showVoting && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <VotingComponent
                feedbackId={feedback.id}
                initialVoteCount={feedback.vote_count}
                initialUserVote={feedback.user_vote}
                size="sm"
              />
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {/* Category Badge */}
              <Badge
                variant="secondary"
                style={{ backgroundColor: `${feedback.category.color}15`, color: feedback.category.color }}
                className="text-xs font-medium"
              >
                {feedback.category.name}
              </Badge>

              {/* Status Badge */}
              {feedback.status.is_public && (
                <Badge
                  variant="outline"
                  style={{ borderColor: feedback.status.color, color: feedback.status.color }}
                  className="text-xs"
                >
                  {feedback.status.name}
                </Badge>
              )}

              {/* Featured Badge */}
              {feedback.is_featured && (
                <Badge variant="default" className="text-xs bg-yellow-500 hover:bg-yellow-600">
                  Featured
                </Badge>
              )}

              {/* Attachments Indicator */}
              {feedback.has_attachments && (
                <Paperclip className="h-3 w-3 text-muted-foreground" />
              )}
            </div>

            {/* Title */}
            <h3 className="font-semibold text-base mb-2 line-clamp-2 leading-tight">
              {feedback.title}
            </h3>

            {/* Description */}
            {feedback.description && (
              <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                {feedback.description}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          {/* Author Info */}
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={feedback.author_profile?.avatar_url} />
              <AvatarFallback className="text-xs">
                {getInitials(feedback.author_profile?.full_name || feedback.author_profile?.username)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">
              {feedback.author_profile?.username || 'Anonymous'}
            </span>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(feedback.created_at)}
            </span>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3">
            {/* Comments */}
            {showComments && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                {feedback.comment_count}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackCard;